// frontend/src/components/schedule/ScheduleView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Calendar, Clock, Users, Plane, Fuel, Wrench, 
  Plus, Edit2, Trash2, X, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, AlertCircle, DollarSign, FileText,
  User, MapPin, Tag, Repeat, Bell, BellOff, MessageSquare,
  Check, ChevronDown, ChevronUp, UserPlus, UserMinus,
  Settings, Eye, EyeOff, Copy, Archive
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { scheduleService } from '../../services/scheduleService';
import { useNotifications, notificationManager } from '../../manager_notifications';
import EventDetailsPanel from './EventDetailsPanel';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const EVENT_TYPES = {
  flight: { label: 'Flight', icon: Plane, color: 'blue', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500' },
  fuel: { label: 'Fuel', icon: Fuel, color: 'orange', bgColor: 'bg-orange-500/20', borderColor: 'border-orange-500' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'red', bgColor: 'bg-red-500/20', borderColor: 'border-red-500' },
  meeting: { label: 'Meeting', icon: Users, color: 'purple', bgColor: 'bg-purple-500/20', borderColor: 'border-purple-500' },
  appointment: { label: 'Appointment', icon: Calendar, color: 'green', bgColor: 'bg-green-500/20', borderColor: 'border-green-500' }
};

const PRIORITIES = {
  low: { label: 'Low', color: 'gray' },
  normal: { label: 'Normal', color: 'blue' },
  high: { label: 'High', color: 'orange' },
  urgent: { label: 'Urgent', color: 'red' }
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const ScheduleView = () => {
  const { user, hasMinLevel } = useAuth();
  const isAdmin = hasMinLevel(90);
  const canManageEvents = hasMinLevel(20);
  
  const { notifications: allNotifications, refresh: refreshNotifications, loading: notifLoading } = useNotifications({
    refreshInterval: 30000
  });
  
  const eventNotifications = useMemo(() => {
    return allNotifications.filter(n => n.source === 'schedule');
  }, [allNotifications]);
  
  // Utiliser useMemo au lieu de useState + useEffect pour éviter la boucle infinie
  const events = useMemo(() => {
    return eventNotifications.map(notif => ({
      id: notif.sourceId,
      title: notif.title,
      description: notif.message,
      event_type: notif.type,
      start_time: notif.timestamp,
      end_time: notif.metadata?.endTime || notif.timestamp,
      priority: notif.priority,
      staff_id: notif.metadata?.staffId,
      staff_first_name: notif.metadata?.staff?.split(' ')[0],
      staff_last_name: notif.metadata?.staff?.split(' ')[1] || '',
      tail_number: notif.metadata?.aircraft,
      client_name: notif.metadata?.client,
      hangar_name: notif.metadata?.hangar,
      check_in: notif.metadata?.checkIn || false,
      check_out: notif.metadata?.checkOut || false,
      billing_status: notif.metadata?.billingStatus || 'pending',
      grading: notif.metadata?.grading || '',
      squawk: notif.metadata?.squawk || '',
      fuel_amount: notif.metadata?.fuelAmount || 0,
      fuel_reimbursed: notif.metadata?.fuelReimbursed || false,
      progress: notif.progress
    }));
  }, [eventNotifications]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [filter, setFilter] = useState({ event_type: null, staff_id: null });
  const [showFilters, setShowFilters] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [hangarsList, setHangarsList] = useState([]);
  const [showEmployeePlanner, setShowEmployeePlanner] = useState(false);
  const [staffLoading, setStaffLoading] = useState(true);

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'flight',
    start_time: '',
    end_time: '',
    priority: 'normal',
    staff_id: '',
    aircraft_id: '',
    client_id: '',
    hangar_id: '',
    check_in: false,
    check_out: false,
    billing_status: 'pending',
    grading: '',
    squawk: '',
    fuel_amount: 0,
    fuel_reimbursed: false
  });

  // Charger les données de référence
  useEffect(() => {
    fetchStaff();
    fetchAircraft();
    fetchClients();
    fetchHangars();
  }, []);

  const fetchStaff = async () => {
    setStaffLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/auth/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      if (error.response?.status === 403) {
        try {
          const token = localStorage.getItem('access_token');
          const response2 = await axios.get(`${API_BASE}/staff`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setStaffList(response2.data);
        } catch (err2) {
          console.error('Alternative staff fetch also failed:', err2);
          setStaffList([]);
        }
      } else {
        setStaffList([]);
      }
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchAircraft = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/aircraft`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAircraftList(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
      setAircraftList([]);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientsList(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsList([]);
    }
  };

  const fetchHangars = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/hangars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHangarsList(response.data);
    } catch (error) {
      console.error('Error fetching hangars:', error);
      setHangarsList([]);
    }
  };

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() - 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
    setPanelOpen(false);
    setSelectedEvent(null);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() + 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
    setPanelOpen(false);
    setSelectedEvent(null);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setPanelOpen(false);
    setSelectedEvent(null);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSelectedEvent(null);
  };

  // Cette fonction n'est plus utilisée pour les PUT car le modal fait déjà le travail
  // Elle sert uniquement à rafraîchir après une action
  const handleRefreshAfterAction = async () => {
    await notificationManager.forceRefresh();
    await refreshNotifications();
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.start_time || !eventForm.end_time) {
      alert('Please fill all required fields');
      return;
    }
    
    if (!eventForm.staff_id) {
      alert('Please assign a staff member to this event');
      return;
    }

    try {
      if (editingEvent) {
        await scheduleService.updateEvent(editingEvent.id, eventForm);
      } else {
        await scheduleService.createEvent(eventForm);
      }
      await notificationManager.forceRefresh();
      await refreshNotifications();
      setShowModal(false);
      resetForm();
      if (panelOpen && selectedEvent && (editingEvent?.id === selectedEvent.id || !editingEvent)) {
        closePanel();
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert(error.response?.data?.detail || 'Error saving event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await scheduleService.deleteEvent(eventId);
        await notificationManager.forceRefresh();
        await refreshNotifications();
        if (selectedEvent && selectedEvent.id === eventId) {
          closePanel();
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        if (error.response?.status === 403) {
          alert('You do not have permission to delete this event. Only managers can delete events.');
        } else {
          alert('Error deleting event: ' + (error.response?.data?.detail || error.message));
        }
      }
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      event_type: 'flight',
      start_time: '',
      end_time: '',
      priority: 'normal',
      staff_id: '',
      aircraft_id: '',
      client_id: '',
      hangar_id: '',
      check_in: false,
      check_out: false,
      billing_status: 'pending',
      grading: '',
      squawk: '',
      fuel_amount: 0,
      fuel_reimbursed: false
    });
  };

  const formatDateHeader = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return currentDate.toLocaleDateString(undefined, options);
  };

  const getEventStyle = (event) => {
    const type = EVENT_TYPES[event.event_type] || EVENT_TYPES.meeting;
    return {
      bgColor: type.bgColor,
      borderColor: type.borderColor,
      icon: type.icon,
      label: type.label,
    };
  };

  const formatTime = (hour) => {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  };

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.getFullYear() === date.getFullYear() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getDate() === date.getDate();
    });
  };

  const renderDayView = () => {
    const day = new Date(currentDate);
    day.setHours(0, 0, 0, 0);
    const dayEvents = getEventsForDay(day);
    
    return (
      <div className="overflow-y-auto max-h-[calc(100vh-250px)]">
        <div className="min-w-[400px]">
          <div className="grid grid-cols-2 border-b border-gray-700 sticky top-0 bg-gray-800/95 z-10">
            <div className="p-3 text-center text-gray-500 text-sm border-r border-gray-700 font-medium w-24">Time</div>
            <div className="p-3 text-center">
              <div className="text-white font-medium">{day.toLocaleDateString('en-US', { weekday: 'long' })}</div>
              <div className="text-gray-400 text-sm">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
            </div>
          </div>
          
          {HOURS.map((hour) => {
            const hourEvents = dayEvents.filter(event => {
              const eventStart = new Date(event.start_time);
              return eventStart.getHours() === hour;
            });
            
            return (
              <div key={hour} className="grid grid-cols-2 border-b border-gray-700 hover:bg-gray-700/30">
                <div className="p-2 text-gray-500 text-sm border-r border-gray-700 w-24">
                  {formatTime(hour)}
                </div>
                <div className="p-1 flex-1 min-h-[60px]">
                  {hourEvents.map((event) => {
                    const style = getEventStyle(event);
                    const EventIcon = style.icon;
                    const eventStart = new Date(event.start_time);
                    const startTime = eventStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        className={`rounded-lg p-2 mb-1 cursor-pointer transition hover:scale-[1.02] ${style.bgColor} border-l-2 ${style.borderColor}`}
                      >
                        <div className="flex items-center gap-2 text-xs">
                          <EventIcon className={`w-3 h-3 text-${EVENT_TYPES[event.event_type]?.color}-400 flex-shrink-0`} />
                          <span className="text-white font-medium truncate flex-1">{event.title}</span>
                          <span className="text-gray-400 text-xs flex-shrink-0 ml-2">{startTime}</span>
                        </div>
                        <div className="text-xs text-gray-400 truncate mt-1">
                          {event.staff_first_name} {event.staff_last_name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startDate = new Date(currentDate);
    const day = startDate.getDay();
    startDate.setDate(startDate.getDate() - day);
    startDate.setHours(0, 0, 0, 0);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    return (
      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-250px)]">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-8 border-b border-gray-700 sticky top-0 bg-gray-800/95 z-10">
            <div className="p-2 text-center text-gray-500 text-sm border-r border-gray-700">Time</div>
            {days.map((day, idx) => (
              <div key={idx} className="p-2 text-center border-l border-gray-700">
                <div className="text-white font-medium">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className="text-gray-400 text-sm">{day.getDate()}</div>
              </div>
            ))}
          </div>
          
          {HOURS.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b border-gray-700 min-h-[60px] hover:bg-gray-700/30">
              <div className="p-2 text-gray-500 text-sm border-r border-gray-700">
                {formatTime(hour)}
              </div>
              {days.map((day, dayIdx) => {
                const dayEvents = getEventsForDay(day);
                const hourEvents = dayEvents.filter(event => {
                  const eventStart = new Date(event.start_time);
                  return eventStart.getHours() === hour;
                });
                return (
                  <div key={dayIdx} className="p-1 border-l border-gray-700 min-h-[60px]">
                    {hourEvents.map((event) => {
                      const style = getEventStyle(event);
                      const EventIcon = style.icon;
                      const eventStart = new Date(event.start_time);
                      const startTime = eventStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      return (
                        <div
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className={`rounded-lg p-1 mb-1 cursor-pointer transition hover:scale-[1.02] ${style.bgColor} border-l-2 ${style.borderColor}`}
                        >
                          <div className="flex items-center gap-1 text-xs">
                            <EventIcon className={`w-3 h-3 text-${EVENT_TYPES[event.event_type]?.color}-400`} />
                            <span className="text-white truncate">{event.title}</span>
                          </div>
                          <div className="text-xs text-gray-400 truncate">
                            {startTime}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {event.staff_first_name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDayOfMonth);
    const startDayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - startDayOfWeek);
    
    const weeks = [];
    let currentDate_iter = new Date(startDate);
    
    while (currentDate_iter <= lastDayOfMonth) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(currentDate_iter));
        currentDate_iter.setDate(currentDate_iter.getDate() + 1);
      }
      weeks.push(week);
    }
    
    return (
      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-250px)]">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-7 border-b border-gray-700 sticky top-0 bg-gray-800/95 z-10">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2 text-center text-gray-400 text-sm border-l border-gray-700">
                {day}
              </div>
            ))}
          </div>
          
          {weeks.map((week, weekIdx) => (
            <div key={weekIdx} className="grid grid-cols-7 border-b border-gray-700">
              {week.map((day, dayIdx) => {
                const isCurrentMonth = day.getMonth() === month;
                const dayEvents = getEventsForDay(day);
                
                return (
                  <div 
                    key={dayIdx} 
                    className={`min-h-[100px] p-1 border-l border-gray-700 ${!isCurrentMonth ? 'bg-gray-900/30' : ''}`}
                  >
                    <div className={`text-right text-sm p-1 ${isCurrentMonth ? 'text-white' : 'text-gray-500'}`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => {
                        const style = getEventStyle(event);
                        const EventIcon = style.icon;
                        return (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            className={`rounded p-1 text-xs cursor-pointer truncate ${style.bgColor} border-l-2 ${style.borderColor}`}
                          >
                            <div className="flex items-center gap-1">
                              <EventIcon className={`w-2 h-2 text-${EVENT_TYPES[event.event_type]?.color}-400`} />
                              <span className="text-white truncate">{event.title}</span>
                            </div>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    if (viewMode === 'day') return renderDayView();
    if (viewMode === 'week') return renderWeekView();
    return renderMonthView();
  };

  // Calculer l'état de chargement global
  const isLoading = notifLoading || staffLoading;

  return (
    <div className="h-full flex">
      <div className={`transition-all duration-300 ${panelOpen ? 'w-3/5' : 'w-full'} flex flex-col h-full`}>
        <div className="bg-gray-800/50 rounded-t-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Schedule</h2>
                <p className="text-gray-400 text-sm">Manage flights, appointments, and team tasks</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-2 text-sm"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              
              {isAdmin && (
                <button
                  onClick={() => setShowEmployeePlanner(!showEmployeePlanner)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition ${
                    showEmployeePlanner ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Staff Planner
                </button>
              )}
              
              {canManageEvents && (
                <button
                  onClick={() => {
                    resetForm();
                    setEventForm({
                      ...eventForm,
                      start_time: new Date().toISOString().slice(0, 16),
                      end_time: new Date(Date.now() + 3600000).toISOString().slice(0, 16)
                    });
                    setShowModal(true);
                  }}
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Event
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-1">
              {['day', 'week', 'month'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition ${
                    viewMode === mode
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <button onClick={handlePrev} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={handleToday} className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
                Today
              </button>
              <button onClick={handleNext} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-3 text-center">
            <h3 className="text-md font-semibold text-white">{formatDateHeader()}</h3>
          </div>

          {showFilters && (
            <div className="mt-3 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select
                  value={filter.event_type || ''}
                  onChange={(e) => setFilter({ ...filter, event_type: e.target.value || null })}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm"
                >
                  <option value="">All Types</option>
                  {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                
                <select
                  value={filter.staff_id || ''}
                  onChange={(e) => setFilter({ ...filter, staff_id: e.target.value || null })}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm"
                >
                  <option value="">All Staff</option>
                  {staffList.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name} ({staff.role})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-800/50 rounded-b-xl border border-gray-700 border-t-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            renderCalendar()
          )}
        </div>

        {isAdmin && showEmployeePlanner && (
          <div className="mt-4 bg-gray-800/50 rounded-xl border border-gray-700 p-4 max-h-[200px] overflow-y-auto">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-3 sticky top-0 bg-gray-800/95 py-2">
              <Users className="w-4 h-4 text-blue-400" />
              Staff Planning
            </h3>
            <div className="overflow-x-auto">
              <div className="flex gap-4 min-w-[600px]">
                {staffList.filter(s => s.is_active).map((staff) => (
                  <div key={staff.id} className="flex-1 min-w-[150px] bg-gray-900/50 rounded-lg p-2">
                    <div className="text-white text-sm font-medium truncate mb-2">
                      {staff.first_name} {staff.last_name}
                    </div>
                    <div className="space-y-1">
                      {events.filter(e => e.staff_id === staff.id).slice(0, 4).map((event) => {
                        const style = getEventStyle(event);
                        const time = new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return (
                          <div key={event.id} className={`text-xs rounded p-1 ${style.bgColor} truncate cursor-pointer`}
                            onClick={() => handleEventClick(event)}>
                            <span className="text-gray-400">{time}</span> {event.title}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Panneau des détails de l'événement - onUpdateStatus ne fait plus de PUT */}
      <EventDetailsPanel
        event={selectedEvent}
        isOpen={panelOpen}
        onClose={closePanel}
        onEdit={(event) => {
          setEditingEvent(event);
          setEventForm({
            title: event.title,
            description: event.description || '',
            event_type: event.event_type,
            start_time: event.start_time?.slice(0, 16) || '',
            end_time: event.end_time?.slice(0, 16) || '',
            priority: event.priority,
            staff_id: event.staff_id || '',
            aircraft_id: event.aircraft_id || '',
            client_id: event.client_id || '',
            hangar_id: event.hangar_id || '',
            check_in: event.check_in || false,
            check_out: event.check_out || false,
            billing_status: event.billing_status || 'pending',
            grading: event.grading || '',
            squawk: event.squawk || '',
            fuel_amount: event.fuel_amount || 0,
            fuel_reimbursed: event.fuel_reimbursed || false
          });
          setShowModal(true);
          closePanel();
        }}
        onDelete={handleDeleteEvent}
        onUpdateStatus={async (eventId, field, value) => {
          // IMPORTANT: Plus de PUT ici ! Le modal EventActionModal fait déjà le PUT.
          // On se contente de rafraîchir les données après l'action.
          await notificationManager.forceRefresh();
          await refreshNotifications();
        }}
        canManageEvents={canManageEvents}
        updatingStatus={false}
      />

      {/* Modal de création/édition */}
      {showModal && canManageEvents && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingEvent ? 'Edit Event' : 'Add Event'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="Event Title *"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />

              <textarea
                placeholder="Description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows="3"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white resize-none"
              />

              <div className="grid grid-cols-2 gap-4">
                <select
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                  {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>

                <select
                  value={eventForm.priority}
                  onChange={(e) => setEventForm({ ...eventForm, priority: e.target.value })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                  {Object.entries(PRIORITIES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={eventForm.start_time}
                    onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">End Time *</label>
                  <input
                    type="datetime-local"
                    value={eventForm.end_time}
                    onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Assign to Staff <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={eventForm.staff_id || ''}
                    onChange={(e) => setEventForm({ ...eventForm, staff_id: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    required
                  >
                    <option value="">-- Select a staff member --</option>
                    {staffList.length === 0 && staffLoading ? (
                      <option value="" disabled>Loading staff...</option>
                    ) : staffList.length === 0 ? (
                      <option value="" disabled>No staff members found</option>
                    ) : (
                      staffList.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.first_name} {staff.last_name} ({staff.role})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <select
                  value={eventForm.aircraft_id}
                  onChange={(e) => setEventForm({ ...eventForm, aircraft_id: e.target.value })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Related Aircraft</option>
                  {aircraftList.map((aircraft) => (
                    <option key={aircraft.id} value={aircraft.id}>
                      {aircraft.tail_number}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <select
                  value={eventForm.client_id}
                  onChange={(e) => setEventForm({ ...eventForm, client_id: e.target.value })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Related Client</option>
                  {clientsList.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name || `${client.first_name} ${client.last_name}`}
                    </option>
                  ))}
                </select>

                <select
                  value={eventForm.hangar_id}
                  onChange={(e) => setEventForm({ ...eventForm, hangar_id: e.target.value })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="">Related Hangar</option>
                  {hangarsList.map((hangar) => (
                    <option key={hangar.id} value={hangar.id}>
                      {hangar.hangar_number} - {hangar.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveEvent} className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white">
                {editingEvent ? 'Update' : 'Create'}
              </button>
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleView;